import { useState, useEffect, useCallback, useId, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, X, ChevronRight, LayoutGrid, List } from 'lucide-react';
import { useProducts, useCategories, useCategory, useSettings } from '@/hooks/useProducts';
import BrandSymbol from '@/components/BrandSymbol';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Pagination from '@/components/ui/Pagination';
import ProductCard from '@/components/ProductCard';
import ProductListItem from '@/components/ProductListItem';
import RecentlyViewed from '@/components/upsell/RecentlyViewed';
import DocumentHead from '@/components/DocumentHead';

export interface AttributeFilter {
  key: string;
  label: string;
  options: { label: string; value: string }[];
}

interface FilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  categories: any[];
  selectedCategories: string[];
  onCategoryChange: (slug: string) => void;
  priceRange: string;
  onPriceChange: (range: string) => void;
  attributeFilters: AttributeFilter[];
  attributeValues: Record<string, string>;
  onAttributeChange: (key: string, value: string) => void;
  onClearAll: () => void;
}

function FilterSidebar({
  isOpen,
  onClose,
  categories,
  selectedCategories,
  onCategoryChange,
  priceRange,
  onPriceChange,
  attributeFilters,
  attributeValues,
  onAttributeChange,
  onClearAll,
}: FilterSidebarProps) {
  const { t } = useTranslation('shop');
  const filterHeadingId = useId();

  const priceRanges = useMemo(
    () => [
      { label: t('priceRanges.under5'), value: '0-5' },
      { label: t('priceRanges.5to10'), value: '5-10' },
      { label: t('priceRanges.10to20'), value: '10-20' },
      { label: t('priceRanges.over20'), value: '20-' },
    ],
    [t],
  );

  const hasActiveFilters =
    selectedCategories.length > 0 || priceRange || Object.values(attributeValues).some(Boolean);

  const content = (
    <div className="space-y-6">
      {hasActiveFilters && (
        <div className="flex justify-end">
          <button
            onClick={onClearAll}
            className="text-sm font-medium text-sage-700 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
          >
            {t('filters.clearAll')}
          </button>
        </div>
      )}

      {/* Category filter */}
      <fieldset>
        <legend className="text-sm font-semibold text-sage-900">{t('filters.category')}</legend>
        <div className="mt-2 space-y-2">
          {categories.map((cat: any) => (
            <label
              key={cat.id}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(cat.slug)}
                onChange={() => onCategoryChange(cat.slug)}
                className="h-4 w-4 rounded border-sage-300 text-sage-600 focus:ring-sage-500"
              />
              <span className="text-sm text-sage-700">{cat.name}</span>
              {cat.product_count != null && (
                <span className="ml-auto text-xs text-sage-400">
                  ({cat.product_count})
                </span>
              )}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Price range filter */}
      <fieldset>
        <legend className="text-sm font-semibold text-sage-900">{t('filters.priceRange')}</legend>
        <div className="mt-2 space-y-2">
          {priceRanges.map((range) => (
            <label
              key={range.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="radio"
                name="priceRange"
                value={range.value}
                checked={priceRange === range.value}
                onChange={() =>
                  onPriceChange(priceRange === range.value ? '' : range.value)
                }
                className="h-4 w-4 border-sage-300 text-sage-600 focus:ring-sage-500"
              />
              <span className="text-sm text-sage-700">{range.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Dynamic attribute filters */}
      {attributeFilters.map((filter) => (
        <fieldset key={filter.key}>
          <legend className="text-sm font-semibold text-sage-900">{filter.label}</legend>
          <div className="mt-2 space-y-2">
            {filter.options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={filter.key}
                  value={opt.value}
                  checked={attributeValues[filter.key] === opt.value}
                  onChange={() =>
                    onAttributeChange(
                      filter.key,
                      attributeValues[filter.key] === opt.value ? '' : opt.value
                    )
                  }
                  className="h-4 w-4 border-sage-300 text-sage-600 focus:ring-sage-500"
                />
                <span className="text-sm text-sage-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:block lg:w-64 lg:shrink-0"
        aria-labelledby={filterHeadingId}
      >
        <h2 id={filterHeadingId} className="text-lg font-semibold text-sage-900 mb-4">
          {t('filters.heading')}
        </h2>
        {content}
      </aside>

      {/* Mobile slide-out overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
            aria-hidden="true"
          />
          <div
            className="absolute inset-y-0 left-0 w-full max-w-xs bg-surface shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label={t('filters.ariaLabel')}
          >
            <div className="flex items-center justify-between border-b border-sage-200 px-4 py-4">
              <h2 className="text-lg font-semibold text-sage-900">{t('filters.heading')}</h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-sage-400 hover:bg-sage-100 hover:text-sage-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                aria-label={t('filters.close')}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 h-[calc(100%-65px)]">
              {content}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * A category's description. Plain text for most; a store brought over from
 * another platform has descriptions written as HTML (cleaned when imported),
 * which would otherwise show their tags.
 */
function CategoryDescription({ text, className }: { text: string; className?: string }) {
  if (/<[a-z][^>]*>/i.test(text)) {
    return (
      <div
        className={cn('prose prose-stone max-w-none prose-a:text-sage-700 prose-img:rounded-lg', className)}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }
  return <p className={cn('text-sage-600', className)}>{text}</p>;
}

export default function Shop() {
  const { t } = useTranslation('shop');
  const { category: categoryParam } = useParams<{ category?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const sort = searchParams.get('sort') || 'newest';

  const sortOptions = useMemo(
    () => [
      { label: t('sort.newest'), value: 'newest' },
      { label: t('sort.priceLowToHigh'), value: 'price_asc' },
      { label: t('sort.priceHighToLow'), value: 'price_desc' },
      { label: t('sort.nameAToZ'), value: 'name_asc' },
    ],
    [t],
  );

  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    categoryParam ? [categoryParam] : []
  );
  const [priceRange, setPriceRange] = useState('');
  const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    () => (localStorage.getItem('shop_view_mode') as 'grid' | 'list') || 'grid'
  );

  const { data: settingsData } = useSettings();

  // Sync category from URL param
  useEffect(() => {
    if (categoryParam) {
      setSelectedCategories([categoryParam]);
    }
  }, [categoryParam]);

  // Read attribute filters from settings (e.g., plant_type, sun_requirement for seed stores)
  const attributeFilters: AttributeFilter[] = settingsData?.settings?.shop_attribute_filters ?? [];

  const filters: Record<string, string> = {};
  if (priceRange) {
    const [min, max] = priceRange.split('-');
    if (min) filters.price_min = min;
    if (max) filters.price_max = max;
  }
  // Apply dynamic attribute filters to API query
  for (const [key, value] of Object.entries(attributeValues)) {
    if (value) filters[key] = value;
  }

  const { data: productsData, isLoading: productsLoading } = useProducts({
    page,
    limit: 20,
    categories: selectedCategories.length > 0 ? selectedCategories : undefined,
    sort,
    filters,
  });

  const { data: categoriesData } = useCategories();
  // The endpoint answers { category, subcategories, breadcrumbs, … }.
  const { data: categoryResponse } = useCategory(categoryParam || '');
  const categoryData = categoryResponse?.category;
  const subcategories: Array<{ id: string; name: string; slug: string }> = categoryResponse?.subcategories ?? [];

  const quickAddEnabled = settingsData?.settings?.shop_quick_add_enabled !== false;

  const products = productsData?.products ?? [];
  // The list answers with its count under `pagination`.
  const totalProducts = productsData?.pagination?.total ?? productsData?.total ?? 0;
  const totalPages = Math.ceil(totalProducts / 20);
  const categories = categoriesData?.categories ?? [];

  const setPage = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(newPage));
      setSearchParams(params);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [searchParams, setSearchParams]
  );

  function handleSortChange(newSort: string) {
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSort);
    params.delete('page');
    setSearchParams(params);
  }

  function handleCategoryToggle(slug: string) {
    setSelectedCategories((prev) =>
      prev.includes(slug)
        ? prev.filter((c) => c !== slug)
        : [...prev, slug]
    );
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    setSearchParams(params);
  }

  function handleViewModeChange(mode: 'grid' | 'list') {
    setViewMode(mode);
    localStorage.setItem('shop_view_mode', mode);
  }

  function handleClearAll() {
    setSelectedCategories([]);
    setPriceRange('');
    setAttributeValues({});
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    setSearchParams(params);
  }

  // Close filter overlay on Escape key
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isFilterOpen) {
        setIsFilterOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFilterOpen]);

  // Lock body scroll when mobile filter is open
  useEffect(() => {
    if (isFilterOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFilterOpen]);

  const documentTitle = categoryData?.name
    ? t('documentHead.categoryTitle', { name: categoryData.name })
    : t('documentHead.title');
  const documentDescription = categoryData?.description || t('documentHead.description');

  return (
    <div className="bg-[--bg-warm] min-h-screen">
      <DocumentHead
        title={documentTitle}
        description={documentDescription}
        canonicalUrl={categoryParam ? `/shop/${categoryParam}` : '/shop'}
      />
      {/* Category hero banner */}
      {categoryParam && categoryData?.image_url && (
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img
            src={categoryData.image_url}
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 overlay-warm" aria-hidden="true" />
          <div className="absolute inset-0 flex items-center justify-center">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              {categoryData.name}
            </h1>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav aria-label={t('breadcrumbs.label')}>
          <ol className="flex items-center gap-1.5 text-sm text-sage-500">
            <li>
              <Link
                to="/"
                className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
              >
                {t('breadcrumbs.home')}
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="h-4 w-4" />
            </li>
            <li>
              <Link
                to="/shop"
                className={cn(
                  'hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded',
                  !categoryParam && 'font-medium text-sage-900'
                )}
                aria-current={!categoryParam ? 'page' : undefined}
              >
                {t('breadcrumbs.shop')}
              </Link>
            </li>
            {categoryParam && categoryData && (
              <>
                <li aria-hidden="true">
                  <ChevronRight className="h-4 w-4" />
                </li>
                <li>
                  <span className="font-medium text-sage-900" aria-current="page">
                    {categoryData.name}
                  </span>
                </li>
              </>
            )}
          </ol>
        </nav>

        {/* Page title — only show if no hero banner */}
        {!(categoryParam && categoryData?.image_url) && (
          <div className="mt-6 mb-8">
            <h1 className="font-display text-3xl font-bold text-sage-900">
              {categoryData?.name || t('allProducts')}
            </h1>
            {categoryData?.description && <CategoryDescription text={categoryData.description} className="mt-2" />}
          </div>
        )}

        {/* Category description when hero is shown */}
        {categoryParam && categoryData?.image_url && categoryData?.description && (
          <CategoryDescription text={categoryData.description} className="mt-6 mb-8 max-w-2xl" />
        )}

        {/* The categories directly beneath this one */}
        {categoryParam && subcategories.length > 0 && (
          <nav aria-label={t('filters.category')} className="mb-8 flex flex-wrap gap-2">
            {subcategories.map((sub) => (
              <Link
                key={sub.id}
                to={`/shop/${sub.slug}`}
                className="rounded-full border border-sage-200 px-3 py-1 text-sm text-sage-800 hover:border-sage-400 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
              >
                {sub.name}
              </Link>
            ))}
          </nav>
        )}

        {/* Toolbar */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="boutique-outline"
              size="sm"
              onClick={() => setIsFilterOpen(true)}
              className="lg:hidden"
              aria-expanded={isFilterOpen}
              aria-controls="filter-panel"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              {t('toolbar.filtersButton')}
            </Button>
            <p className="text-sm text-sage-500">
              {t('toolbar.productCount', { count: totalProducts })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-sage-200 rounded-lg overflow-hidden">
              <button
                onClick={() => handleViewModeChange('grid')}
                className={cn(
                  'p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
                  viewMode === 'grid' ? 'bg-cta text-cta-fg' : 'bg-surface text-sage-500 hover:text-sage-700'
                )}
                aria-label={t('toolbar.gridView')}
                aria-pressed={viewMode === 'grid'}
              >
                <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                onClick={() => handleViewModeChange('list')}
                className={cn(
                  'p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
                  viewMode === 'list' ? 'bg-cta text-cta-fg' : 'bg-surface text-sage-500 hover:text-sage-700'
                )}
                aria-label={t('toolbar.listView')}
                aria-pressed={viewMode === 'list'}
              >
                <List className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="w-48">
              <Select
                label=""
                options={sortOptions}
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
                aria-label={t('toolbar.sortLabel')}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex gap-8">
          <FilterSidebar
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryToggle}
            priceRange={priceRange}
            onPriceChange={setPriceRange}
            attributeFilters={attributeFilters}
            attributeValues={attributeValues}
            onAttributeChange={(key, value) =>
              setAttributeValues((prev) => ({ ...prev, [key]: value }))
            }
            onClearAll={handleClearAll}
          />

          <div className="flex-1">
            {productsLoading ? (
              <div
                className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                aria-busy="true"
                aria-label={t('results.loadingAriaLabel')}
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse" aria-hidden="true">
                    <div className="aspect-[3/4] rounded-lg bg-sage-100" />
                    <div className="mt-3 h-4 rounded bg-sage-100 w-3/4" />
                    <div className="mt-2 h-4 rounded bg-sage-100 w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="py-16 text-center">
                <BrandSymbol className="mx-auto h-12 w-12 text-sage-300" />
                <h2 className="mt-4 text-lg font-semibold text-sage-900">
                  {t('results.emptyHeading')}
                </h2>
                <p className="mt-2 text-sage-600">
                  {t('results.emptyDescription')}
                </p>
                <Button
                  variant="boutique-outline"
                  className="mt-4"
                  onClick={handleClearAll}
                >
                  {t('filters.clearAll')}
                </Button>
              </div>
            ) : (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {products.map((product: any) => (
                      <ProductCard key={product.id} product={product} showQuickAdd={quickAddEnabled} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-surface rounded-lg border border-sage-200 divide-y divide-sage-100">
                    {products.map((product: any) => (
                      <ProductListItem key={product.id} product={product} showQuickAdd={quickAddEnabled} />
                    ))}
                  </div>
                )}
                <div className="mt-8 flex justify-center">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recently Viewed */}
        <RecentlyViewed />
      </main>
    </div>
  );
}
